import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ChangeRequestService } from '../../services/change-request.service';
import { AuthService } from '../../../services/auth.service';

@Component({
  selector: 'app-asc-chgrequest',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './asc-chgrequest.html',
  styleUrl: './asc-chgrequest.scss'
})
export class AscChgrequestComponent implements OnInit {
  // 親コンポーネントへのイベント
  @Output() navigateToJobOrder = new EventEmitter<number>();
  @Output() navigateToAsc = new EventEmitter<string>();
  @Output() navigateToCustomer = new EventEmitter<number>();
  @Output() navigateToTransferWithRequestId = new EventEmitter<string>();
  // ブロック1: 未処理リスト
  showUnprocessedBlock: boolean = true;
  unprocessedRequests: any[] = [];
  isLoadingUnprocessed: boolean = false;
  
  // ページング（未処理）
  unprocessedPageNo: number = 1;
  unprocessedPageSize: number = 50;
  unprocessedTotalRecords: number = 0;
  unprocessedTotalPages: number = 0;

  // ブロック2: 詳細表示
  showDetailBlock: boolean = false;
  selectedRequest: any = null;
  isLoadingDetail: boolean = false;

  // ブロック2: 処理エリアのフォーム
  billpayOption: string = ''; // 0:取消せない, 1:短縮, 2:依頼通り
  shortDateValue: string = ''; // 取消可能な日まで短縮の日付
  powerbaseOption: string = ''; // 0:全削除, 1:期間変更+TS削除, 2:TS削除のみ
  statusOption: string = ''; // 1:BillPay確認中, 2:担当者問合せ中, 3:Pending, 4:その他, 5:確認完了, 6:確定待ち
  processComment: string = '';
  
  // 変更実行と別処理ボタンの有効/無効状態
  isExecuteButtonEnabled: boolean = false;
  isAlternativeButtonEnabled: boolean = false;

  // BillPayの短縮オプションが選択できない状態（削除変更理由が「獲注削除」の場合）
  isBillpayShortenDisabled: boolean = false;
  
  // POWER Base Checkの有効/無効状態
  isPowerBaseCheckEnabled: boolean = true;
  
  // 確認完了の有効/無効状態
  isConfirmCompleteEnabled: boolean = false;
  
  // 確定待ちフラグ（ProcStatus = 6 の場合に true）
  isPendingConfirmation: boolean = false;
  
  // 保存成功メッセージ
  saveSuccessMessage: string = '';

  // ブロック3: 処理済リスト
  completedRequests: any[] = [];
  isLoadingCompleted: boolean = false;
  selectedPeriodDays: number = 2; // 1:本日, 2:1週間, 3:1か月（初期値: 1週間）
  
  // ページング(処理済)
  completedPageNo: number = 1;
  completedPageSize: number = 20;
  completedTotalRecords: number = 0;
  completedTotalPages: number = 0;

  // 初回ロードフラグ
  private isFirstLoad: boolean = true;

  constructor(
    private changeRequestService: ChangeRequestService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    // タブクリック時に初回ロードするため、ここでは何もしない
  }

  // コンポーネントが表示された時に呼ばれる（親から呼び出す）
  onTabActivated(): void {
    if (this.isFirstLoad) {
      this.loadUnprocessedRequests();
      this.loadCompletedRequests();
      this.isFirstLoad = false;
    }
  }

  // 未処理リストを読み込み
  loadUnprocessedRequests(): void {
    this.isLoadingUnprocessed = true;
    
    this.changeRequestService.getAllChangeRequests(0, this.unprocessedPageNo, this.unprocessedPageSize, 0).subscribe({
      next: (response) => {
        this.unprocessedRequests = response.requests || [];
        this.unprocessedTotalRecords = response.totalRecords || 0;
        this.unprocessedTotalPages = Math.ceil(this.unprocessedTotalRecords / this.unprocessedPageSize);
        this.isLoadingUnprocessed = false;
      },
      error: (error) => {
        console.error('未処理リストの取得に失敗しました:', error);
        this.isLoadingUnprocessed = false;
        alert('データの取得に失敗しました。');
      }
    });
  }

  // ページ変更（未処理）
  onUnprocessedPageChange(pageNo: number): void {
    if (pageNo < 1 || pageNo > this.unprocessedTotalPages) {
      return;
    }
    this.unprocessedPageNo = pageNo;
    this.loadUnprocessedRequests();
  }

  // ページ番号配列を取得（未処理）
  getUnprocessedPageNumbers(): number[] {
    const pages: number[] = [];
    const maxVisible = 10;
    
    if (this.unprocessedTotalPages <= maxVisible) {
      for (let i = 1; i <= this.unprocessedTotalPages; i++) {
        pages.push(i);
      }
    } else {
      const half = Math.floor(maxVisible / 2);
      let start = Math.max(1, this.unprocessedPageNo - half);
      let end = Math.min(this.unprocessedTotalPages, start + maxVisible - 1);
      
      if (end - start < maxVisible - 1) {
        start = Math.max(1, end - maxVisible + 1);
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
    }
    
    return pages;
  }

  // ページ番号配列を取得（処理済）
  getCompletedPageNumbers(): number[] {
    const pages: number[] = [];
    const maxVisible = 10;
    
    if (this.completedTotalPages <= maxVisible) {
      for (let i = 1; i <= this.completedTotalPages; i++) {
        pages.push(i);
      }
    } else {
      const half = Math.floor(maxVisible / 2);
      let start = Math.max(1, this.completedPageNo - half);
      let end = Math.min(this.completedTotalPages, start + maxVisible - 1);
      
      if (end - start < maxVisible - 1) {
        start = Math.max(1, end - maxVisible + 1);
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
    }
    
    return pages;
  }

  // ブロック3: 処理済リストを読み込み
  loadCompletedRequests(): void {
    this.isLoadingCompleted = true;
    
    this.changeRequestService.getAllChangeRequests(1, this.completedPageNo, this.completedPageSize, this.selectedPeriodDays).subscribe({
      next: (response) => {
        this.completedRequests = response.requests || [];
        this.completedTotalRecords = response.totalRecords || 0;
        this.completedTotalPages = Math.ceil(this.completedTotalRecords / this.completedPageSize);
        this.isLoadingCompleted = false;
      },
      error: (error) => {
        console.error('処理済リストの取得に失敗しました:', error);
        this.isLoadingCompleted = false;
        alert('データの取得に失敗しました。');
      }
    });
  }

  // 期間オプション変更
  onPeriodChange(days: number): void {
    this.selectedPeriodDays = days;
    this.completedPageNo = 1;
    this.loadCompletedRequests();
  }

  // ページ変更(処理済)
  onCompletedPageChange(pageNo: number): void {
    if (pageNo < 1 || pageNo > this.completedTotalPages) {
      return;
    }
    this.completedPageNo = pageNo;
    this.loadCompletedRequests();
  }

  // CSV出力
  onExportCSV(): void {
    this.changeRequestService.getCompletedRequestsForExport(this.selectedPeriodDays).subscribe({
      next: (response) => {
        const data = response.requests || [];
        this.exportToCSV(data);
      },
      error: (error) => {
        console.error('CSV出力エラー:', error);
        alert('CSV出力に失敗しました。');
      }
    });
  }

  // CSVファイル作成
  exportToCSV(data: any[]): void {
    const headers = [
      'No', '獲注番号', 'ASC登録番号', '部署コード', '元開始日', '元終了日',
      '削除開始日', '確定削除日', '変更理由', '発生日', '前払い', '状態',
      '支店担当者', '処理コード', '処理状況', '処理担当者', '処理更新日時', '処理コメント'
    ];
    
    let csv = '\uFEFF' + headers.join(',') + '\n'; // BOM追加でExcel対応
    
    data.forEach(row => {
      const values = [
        row.requestID || '',
        row.jobOrderID || '',
        row.userCode || '',
        row.customerOfficeCode || '',
        this.formatDateForCSV(row.oldStartDate),
        this.formatDateForCSV(row.oldEndDate),
        this.formatDateForCSV(row.cutDate),
        this.formatDateForCSV(row.fixCutDate),
        this.escapeCSV(row.chgReason || ''),
        this.formatDateForCSV(row.createDate),
        row.prePay || '',
        row.dataStatus || '',
        this.escapeCSV(row.repName || ''),
        row.procCode || '',
        this.escapeCSV(row.procStatus || ''),
        this.escapeCSV(row.procUserName || ''),
        row.procDate || '',
        this.escapeCSV(row.procComment || '')
      ];
      csv += values.join(',') + '\n';
    });
    
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const periodName = this.selectedPeriodDays === 1 ? '本日' : this.selectedPeriodDays === 2 ? '1週間' : '1か月';
    link.setAttribute('href', url);
    link.setAttribute('download', `変更依頼リスト_処理済_${periodName}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  // CSV用エスケープ処理
  escapeCSV(value: string): string {
    if (value.includes(',') || value.includes('"') || value.includes('\n')) {
      return '"' + value.replace(/"/g, '""') + '"';
    }
    return value;
  }

  // CSV用日付フォーマット
  formatDateForCSV(dateStr: string): string {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}/${month}/${day}`;
  }

  // 獲注番号タブを開く
  onShowJobOrder(jobOrderID: number): void {
    this.navigateToJobOrder.emit(jobOrderID);
  }

  // ASC情報タブを開く
  onShowASCInfo(userCode: string): void {
    this.navigateToAsc.emit(userCode);
  }

  // 顧客部署情報タブを開く
  onShowCustomerInfo(customerOfficeCode: string): void {
    // selectedRequestからCustomerOfficeIDを取得
    if (this.selectedRequest && this.selectedRequest.customerOfficeID) {
      this.navigateToCustomer.emit(this.selectedRequest.customerOfficeID);
    } else {
      console.warn('CustomerOfficeIDが見つかりません:', customerOfficeCode);
    }
  }

  // 表示ボタンクリック
  onShowDetail(request: any): void {
    this.isLoadingDetail = true;
    this.showDetailBlock = true;
    this.showUnprocessedBlock = false;
    
    this.changeRequestService.getChangeRequestDetail(request.requestID).subscribe({
      next: (detail) => {
        this.selectedRequest = detail;
        this.resetProcessForm();
        
        // ボタンの有効化状態を更新
        this.updateButtonStates();
        
        this.isLoadingDetail = false;
      },
      error: (error) => {
        console.error('詳細の取得に失敗しました:', error);
        this.isLoadingDetail = false;
        alert('詳細の取得に失敗しました。');
      }
    });
  }

  // 処理フォームをリセット
  resetProcessForm(): void {
    if (!this.selectedRequest) {
      this.billpayOption = '';
      this.shortDateValue = '';
      this.powerbaseOption = '';
      this.statusOption = '';
      this.processComment = '';
      this.isPendingConfirmation = false;
      return;
    }

    // bPcheck の値を使用（NULL = 未選択、0-2 = 選択）
    const bpcheck = this.selectedRequest.bPcheck;
    if (bpcheck !== null && bpcheck !== undefined) {
      this.billpayOption = String(bpcheck);
    } else {
      this.billpayOption = '';
    }
    
    // FixCutDate の値を使用（BillPay Check = 1 の場合のみ）
    const fixCutDate = this.selectedRequest.fixCutDate ?? this.selectedRequest.FixCutDate;
    if (this.billpayOption === '1' && fixCutDate) {
      this.shortDateValue = fixCutDate;
    } else {
      this.shortDateValue = '';
    }
    
    // 削除/変更種類から自動決定（獲注削除→全削除、期間短縮→獲注期間はそのままでTS削除）
    this.powerbaseOption = this.getPowerbaseOptionFromChgReason();
    
    // ProcStatusCode の値を使用（NULL または 0 = 未選択、1-5 = 選択）
    const procStatusCode = this.selectedRequest.procStatusCode ?? this.selectedRequest.ProcStatusCode;
    if (procStatusCode !== null && procStatusCode !== undefined && procStatusCode > 0) {
      this.statusOption = String(procStatusCode);
    } else {
      this.statusOption = '';
    }
    
    // 確定待ちフラグを設定（ProcStatus = 6 の場合）
    this.isPendingConfirmation = procStatusCode === 6;
    
    // ProcComment の値を使用
    const procComment = this.selectedRequest.procComment ?? this.selectedRequest.ProcComment;
    this.processComment = procComment || '';
    
    // POWER Base Checkの有効/無効を更新
    this.updatePowerBaseCheckState();
    
    // 確認完了の有効/無効を更新
    this.updateConfirmCompleteState();
  }

  // 契約期間の表示（加工1）
  getOldPeriod(): string {
    if (!this.selectedRequest) return '';
    return `${this.selectedRequest.oldStartDate}～${this.selectedRequest.oldEndDate}`;
  }

  // 更新契約期間の表示（加工2）
  getNewPeriod(): string {
    if (!this.selectedRequest) return '';

    // BillPay Check: BPのデータは取消せない(0)
    if (this.billpayOption === '0') {
      return '-';
    }

    // BillPay Check: 取消可能な日まで短縮(1)
    if (this.billpayOption === '1') {
      if (!this.shortDateValue) return '';
      const fixDate = new Date(this.shortDateValue.replace(/\//g, '-'));
      if (isNaN(fixDate.getTime())) return '';
      fixDate.setDate(fixDate.getDate() - 1);
      return `${this.selectedRequest.oldStartDate}～${this.formatDate(fixDate)}`;
    }

    // BillPay Check: 依頼通りに短縮/削除(2) または未選択 → 更新契約期間と同じ内容
    const oldStart = new Date(this.selectedRequest.oldStartDate);
    const cutDate = new Date(this.selectedRequest.cutDate);

    if (oldStart >= cutDate) {
      return '全削除';
    }

    // cutDate - 1日
    const endDate = new Date(cutDate);
    endDate.setDate(endDate.getDate() - 1);
    const formattedEnd = this.formatDate(endDate);

    return `${this.selectedRequest.oldStartDate}～${formattedEnd}`;
  }

  // 日付フォーマット（yyyy/MM/dd）
  formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}/${month}/${day}`;
  }

  // DataStatusに応じたメッセージ
  getStatusMessage(): string {
    if (!this.selectedRequest) return '';

    const code = this.selectedRequest.dataStatusCode ?? null;
    const statusMessages: { [key: number]: string } = {
      0: '削除可能な状態です',
      1: '削除開始日以降のTSに保存済が含まれます',
      2: '削除開始日以降のTSに報告済が含まれます',
      3: '削除開始日以降のTSに承認済が含まれます',
      4: '削除開始日以降のTSはBP取込済みの可能性があります'
    };

    return code !== null ? (statusMessages[code] || '') : '';
  }

  // 保存ボタンクリック
  onSaveProcess(): void {
    if (!this.selectedRequest) {
      alert('データが選択されていません。');
      return;
    }

    // バリデーション: BillPay Check「取消可能な日まで短縮」が選択されている場合
    if (this.billpayOption === '1') {
      if (!this.shortDateValue || this.shortDateValue.trim() === '') {
        alert('取消可能な日まで短縮が選択されています。日付を入力してください。');
        return;
      }

      // 日付形式の検証 (yyyy/mm/dd)
      const datePattern = /^\d{4}\/\d{1,2}\/\d{1,2}$/;
      if (!datePattern.test(this.shortDateValue)) {
        alert('日付はyyyy/mm/dd形式で入力してください。');
        return;
      }

      // 日付の妥当性チェック
      const dateParts = this.shortDateValue.split('/');
      const year = parseInt(dateParts[0], 10);
      const month = parseInt(dateParts[1], 10) - 1; // JavaScriptの月は0-11
      const day = parseInt(dateParts[2], 10);
      const dateObj = new Date(year, month, day);

      if (dateObj.getFullYear() !== year || 
          dateObj.getMonth() !== month || 
          dateObj.getDate() !== day) {
        alert('正しい日付を入力してください。');
        return;
      }
    }

    // 確認
    if (!confirm('処理状況を保存します。よろしいですか？')) {
      return;
    }

    const requestId = this.selectedRequest.requestID ?? this.selectedRequest.RequestID;
    const saveData = {
      bpcheck: this.billpayOption ? parseInt(this.billpayOption, 10) : null,
      procCode: this.powerbaseOption ? parseInt(this.powerbaseOption, 10) : null,
      procStatus: this.statusOption ? parseInt(this.statusOption, 10) : null,
      procComment: this.processComment || null,
      fixCutDate: (this.billpayOption === '1' && this.shortDateValue) ? this.shortDateValue : null
    };

    this.changeRequestService.saveProcessStatus(requestId, saveData).subscribe({
      next: (response) => {
        // 保存成功メッセージを表示
        this.saveSuccessMessage = '処理状況を保存しました。';
        
        // ボタンの有効化状態を更新
        this.updateButtonStates();
        
        // リストを再読み込み
        this.loadUnprocessedRequests();
      },
      error: (error) => {
        console.error('保存エラー:', error);
        this.saveSuccessMessage = '保存に失敗しました。';
      }
    });
  }

  // 変更実行ボタンクリック
  onExecuteChange(): void {
    this.clearSaveMessage();
    
    // 確認ダイアログを表示
    const confirmed = confirm('この変更申請について削除/変更処理を行います。よろしいですか?');
    if (!confirmed) {
      return;
    }

    // 変更実行APIを呼び出し
    this.changeRequestService.executeChange(this.selectedRequest!.requestID).subscribe({
      next: (response) => {
        if (response.status) {
          // 処理成功
          // 処理完了後、詳細ブロックを非表示にして未処理リストを表示
          this.showDetailBlock = false;
          this.showUnprocessedBlock = true;
          
          // 両方のリストを再読み込み
          this.loadUnprocessedRequests();
          this.loadCompletedRequests();
        } else {
          // 処理失敗 - サーバーから返されたエラーメッセージを表示
          alert(response.message || '変更実行に失敗しました。');
        }
      },
      error: (err) => {
        console.error('変更実行エラー:', err);
        alert('変更実行中にエラーが発生しました: ' + (err.error?.message || err.message));
      }
    });
  }

  // 別処理ボタンクリック
  onAlternativeProcess(): void {
    this.clearSaveMessage();
    
    // 確認ダイアログを表示
    const confirmed = confirm('この変更申請について変更せずに完了とします。よろしいですか?');
    if (!confirmed) {
      return;
    }

    // 別処理APIを呼び出し
    this.changeRequestService.alternativeProcess(this.selectedRequest!.requestID).subscribe({
      next: (response) => {
        if (response.status) {
          // 処理成功
          // 処理完了後、詳細ブロックを非表示にして未処理リストを表示
          this.showDetailBlock = false;
          this.showUnprocessedBlock = true;
          
          // 両方のリストを再読み込み
          this.loadUnprocessedRequests();
          this.loadCompletedRequests();
        } else {
          // 処理失敗 - サーバーから返されたエラーメッセージを表示
          alert(response.message || '別処理に失敗しました。');
        }
      },
      error: (err) => {
        console.error('別処理エラー:', err);
        alert('別処理中にエラーが発生しました: ' + (err.error?.message || err.message));
      }
    });
  }

  // 閉じるボタンクリック
  onCloseDetail(): void {
    this.clearSaveMessage();
    this.showDetailBlock = false;
    this.showUnprocessedBlock = true;
    this.selectedRequest = null;
    this.isPendingConfirmation = false;
  }

  // 申請取消ボタンクリック
  onCancelRequest(): void {
    if (!confirm('この申請を取消します。よろしいですか？')) {
      return;
    }

    const requestId = this.selectedRequest?.requestID;
    if (!requestId) {
      alert('申請IDが取得できません。');
      return;
    }

    this.changeRequestService.cancelChangeRequestById(requestId)
      .subscribe({
        next: () => {
          alert('申請を取消しました。');
          this.onCloseDetail();
          this.loadUnprocessedRequests();
        },
        error: (error: any) => {
          const message = error?.error?.message || '申請の取消中にエラーが発生しました。';
          alert(`エラー: ${message}`);
        }
      });
  }

  // 強制完了ボタンクリック
  onForceComplete(): void {
    if (!confirm('この申請を完了済みに変更します。よろしいですか？')) {
      return;
    }

    const requestId = this.selectedRequest?.requestID;
    if (!requestId) {
      alert('申請IDが取得できません。');
      return;
    }

    this.changeRequestService.forceComplete(requestId)
      .subscribe({
        next: (response: any) => {
          if (response.status) {
            alert('申請を完了済みに変更しました。');
            // 画面をリロード
            this.onCloseDetail();
            this.loadUnprocessedRequests();
            this.loadCompletedRequests();
          } else {
            alert(`エラー: ${response.message || '強制完了に失敗しました。'}`);
          }
        },
        error: (error: any) => {
          console.error('強制完了エラー:', error);
          alert('強制完了処理中にエラーが発生しました。');
        }
      });
  }

  // BillPay Checkの変更イベント
  onBillpayOptionChange(): void {
    this.clearSaveMessage();
    this.updatePowerBaseCheckState();
    this.updateConfirmCompleteState();
    this.updateAllDeleteOptionState();
  }

  // 短縮日付の自動フォーマット（yyyy/mm/dd形式）
  formatShortDateInput(event: Event): void {
    if ((event as InputEvent).isComposing) return;
    const input = event.target as HTMLInputElement;
    let value = input.value.replace(/\D/g, ''); // 数字以外を削除
    
    if (value.length >= 8) {
      value = value.substring(0, 8);
      this.shortDateValue = `${value.substring(0, 4)}/${value.substring(4, 6)}/${value.substring(6, 8)}`;
    } else if (value.length >= 6) {
      this.shortDateValue = `${value.substring(0, 4)}/${value.substring(4, 6)}/${value.substring(6)}`;
    } else if (value.length >= 4) {
      this.shortDateValue = `${value.substring(0, 4)}/${value.substring(4)}`;
    } else {
      this.shortDateValue = value;
    }
  }

  // POWER Base Checkの変更イベント
  onPowerbaseOptionChange(): void {
    this.clearSaveMessage();
    this.updateConfirmCompleteState();
  }

  // POWER Base Checkの有効/無効を更新
  updatePowerBaseCheckState(): void {
    // BillPayが「BPのデータは取り消せない」(0)の場合、POWER Base Checkを無効化
    this.isPowerBaseCheckEnabled = this.billpayOption !== '0';
    
    // powerbaseOptionは削除/変更種類から自動決定するため常に再設定
    this.powerbaseOption = this.getPowerbaseOptionFromChgReason();
  }

  // 「全削除」オプションの有効/無効を更新
  updateAllDeleteOptionState(): void {
    // powerbaseOptionは削除/変更種類から自動決定するため常に再設定
    this.powerbaseOption = this.getPowerbaseOptionFromChgReason();
  }

  // 削除/変更種類からpowerbaseOptionを自動決定
  getPowerbaseOptionFromChgReason(): string {
    const chgReason = this.selectedRequest?.chgReason;
    if (chgReason === '獲注削除') return '0';  // 全削除
    if (chgReason === '期間短縮') return '2';  // 獲注期間はそのままでTS削除
    return '';
  }

  // 「全削除」オプションが選択可能かどうかを判定
  isAllDeleteOptionEnabled(): boolean {
    // 更新契約期間が「全削除」かどうかをチェック
    const isAllDelete = this.getNewPeriod() === '全削除';
    
    // BillPay Checkで「依頼通りに短縮/削除」(2)が選択されているか、何も選択されていない
    const isBillpayAllowed = this.billpayOption === '' || this.billpayOption === '2';
    
    return isAllDelete && isBillpayAllowed;
  }

  // 確認完了の有効/無効を更新
  updateConfirmCompleteState(): void {
    // BillPay Checkが選択されているか確認
    const billpaySelected = this.billpayOption !== '';
    
    // BillPay Check = 0 (BPのデータは取消せない) の場合
    const isBillpayZero = this.billpayOption === '0';
    
    // BillPay Check = 0の場合は、BillPay Checkのみで確認完了を有効化
    // それ以外の場合は、BillPay CheckとPOWER Base Checkの両方が必要
    let confirmCompleteAllowed = false;
    if (isBillpayZero) {
      confirmCompleteAllowed = billpaySelected;
    } else {
      const powerbaseSelected = this.powerbaseOption !== '';
      confirmCompleteAllowed = billpaySelected && powerbaseSelected;
    }
    
    // または、データ取得時にbPcheckとProcCodeが両方NULL以外
    const bpcheck = this.selectedRequest?.bPcheck;
    const procCode = this.selectedRequest?.procCode ?? this.selectedRequest?.ProcCode;
    const hasExistingData = (bpcheck !== null && bpcheck !== undefined) && 
                            (procCode !== null && procCode !== undefined);
    
    this.isConfirmCompleteEnabled = confirmCompleteAllowed || hasExistingData;
    
    // 確認完了が無効になった場合、statusOptionが5ならクリア
    if (!this.isConfirmCompleteEnabled && this.statusOption === '5') {
      this.statusOption = '';
    }
  }

  // ボタンの有効化状態を更新
  updateButtonStates(): void {
    const procStatusCode = this.selectedRequest?.procStatusCode ?? this.selectedRequest?.ProcStatusCode;
    const bpcheck = this.selectedRequest?.bPcheck;

    // 追加: 削除変更種類が「獲注削除」なら BillPay value=1 を禁止
    const chgReason = this.selectedRequest?.chgReason ?? this.selectedRequest?.ChgReason;
    this.isBillpayShortenDisabled = chgReason === '獲注削除';

    // すでに1が選ばれていた場合は解除
    if (this.isBillpayShortenDisabled && this.billpayOption === '1') {
      this.billpayOption = '';
      this.shortDateValue = '';
    }
    
    // 現在のフォームの値も考慮
    const currentBPcheck = this.billpayOption ? parseInt(this.billpayOption, 10) : bpcheck;
    const currentProcStatus = this.statusOption ? parseInt(this.statusOption, 10) : procStatusCode;
    
    if (currentProcStatus === 5) {
      if (currentBPcheck === 0) {
        // bPcheck=0の場合、別処理のみ有効
        this.isExecuteButtonEnabled = false;
        this.isAlternativeButtonEnabled = true;
      } else if (currentBPcheck !== null && currentBPcheck !== undefined && currentBPcheck > 0) {
        // BPcheck>0の場合、両方有効
        this.isExecuteButtonEnabled = true;
        this.isAlternativeButtonEnabled = true;
      } else {
        // BPcheckがnullの場合、両方無効
        this.isExecuteButtonEnabled = false;
        this.isAlternativeButtonEnabled = false;
      }
    } else {
      // ProcStatus≠5の場合、両方無効
      this.isExecuteButtonEnabled = false;
      this.isAlternativeButtonEnabled = false;
    }
  }

  // 保存成功メッセージをクリア
  clearSaveMessage(): void {
    this.saveSuccessMessage = '';
  }

  // 転記処理タブに遷移
  navigateToTransfer(requestID: number): void {
    const requestIdStr = 'R' + requestID;
    this.navigateToTransferWithRequestId.emit(requestIdStr);
  }
}

